import {
  INVOICE_STATUSES,
  formatMoney,
  type Invoice,
  type InvoiceStatus,
  type Metafield,
} from '@forecastify/shared';
import { PersonFace } from '../person-face';
import { useState } from 'react';
import { Badge, DropdownMenu, Icon, Tooltip } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Attachments } from '../attachments';
import { COMMENT_PROSE, CommentComposer } from '../tasks/comment-composer';
import {
  DetailFrame,
  InlineTitle,
  MoreMenu,
  RailCard,
  RailDate,
  RailMoney,
  RailPicker,
  RailRow,
  RailValue,
  SectionHeader,
} from './detail-chrome';
import { MetafieldsCard } from './metafields-card';
import { CollapsibleHistory } from '../history/collapsible-history';
import { statusLabel, targetLabel } from './invoice-list';
import { RailEditLink } from './rail-edit-link';
import type { InvoicePatch } from './use-invoices';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C57 · A számla — `invoice-detail.png`, transcribed.                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE TITLE IS THEIR NUMBER, NOT OURS.** Our `inv_21b5a1e2` sits in grey above (R-57); the
 * 19px heading is `LN-2026-0388`, because the number a person reconciles against a bank statement
 * is the accounting system's. Getting this the way round it looks obvious in a schema — ours is
 * the primary key — makes the screen useless for the one job it has.
 *
 * ⚠️ **The rail is ÖSSZEG then RÉSZLETEK and nothing else.** The mockup's own view model computes a
 * *billed against revenue* progress block for this screen (`billRevenue`, `billPct`, `billNote`)
 * and the markup draws none of it — one of the 140 keys `markup.html` renders nowhere. That
 * comparison belongs on the SOURCE, where the revenue it is a share of also lives, and it is drawn
 * there.
 */

export interface InvoiceDetailData {
  readonly invoice: Invoice;
  /** C30 · the workspace's `invoice` metafields. Empty when none are defined. */
  readonly metafields: readonly Metafield[];
  readonly members: readonly { readonly id: string; readonly name: string }[];
  readonly currencies: readonly { readonly code: string; readonly symbol: string }[];
}

export interface InvoiceDetailHandlers {
  readonly canManage: boolean;
  readonly onStatus: (status: InvoiceStatus) => void;
  /**
   * ⚠️ ux-pass-2 · one field changed in place — the number, the amount, the date, the owner, the
   * note. The caller merges it onto the record and sends the whole body with its `version`.
   */
  readonly onPatch: (change: InvoicePatch) => void;
  /** What it bills, in `InvoiceDialog`'s `focus="target"` — a kind and a source together. */
  readonly onEditTarget: () => void;
  readonly onDelete: () => void;
  readonly onMetafields: (values: Record<string, unknown>) => void;
  readonly onOpenProject: () => void;
  readonly onOpenTarget: () => void;
  readonly onAttach: (file: File) => Promise<string | null>;
}

/** ⚠️ The mockup's own `INV_ST` colours. */
const STATUS_DOT: Record<InvoiceStatus, string> = {
  draft: 'var(--status-idle)',
  issued: 'var(--status-info)',
  paid: 'var(--status-ok)',
  void: 'var(--status-idle)',
};

export function InvoiceDetail({
  data,
  handlers,
  wide,
}: {
  readonly data: InvoiceDetailData;
  readonly handlers: InvoiceDetailHandlers;
  readonly wide: boolean;
}) {
  const t = useT();
  const invoice = data.invoice;

  const symbol =
    data.currencies.find((one) => one.code === invoice.currencyCode)?.symbol ??
    invoice.currencyCode;
  const amount = formatMoney(Number(invoice.amount), {
    code: invoice.currencyCode,
    symbol,
  });

  return (
    <DetailFrame
      wide={wide}
      header={
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-[12px] tabular-nums">
              {invoice.identifier}
            </span>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-[12px] transition-colors"
              onClick={handlers.onOpenProject}
            >
              <span
                className="size-2 rounded-[3px]"
                style={{ background: invoice.projectColor ?? 'var(--status-idle)' }}
              />
              {invoice.projectName}
            </button>

            {/* ⚠️ The badge IS the control — the mockup makes the status pill a menu rather than
                putting a separate picker in the rail, and a status is the thing most often changed
                on this screen. */}
            {handlers.canManage ? (
              <DropdownMenu
                width={200}
                align="start"
                label={t('invoice.action.status')}
                items={INVOICE_STATUSES.map((status) => ({
                  label: statusLabel(t, status),
                  dot: STATUS_DOT[status],
                  checked: invoice.status === status,
                  onSelect: () => {
                    handlers.onStatus(status);
                  },
                }))}
              >
                <button
                  type="button"
                  className="bg-muted flex h-[22px] items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium"
                >
                  <span
                    className="size-2 rounded-full"
                    style={{ background: STATUS_DOT[invoice.status] }}
                  />
                  {statusLabel(t, invoice.status)}
                  <Icon name="chevron-down" size={11} className="opacity-60" />
                </button>
              </DropdownMenu>
            ) : (
              <Badge variant="secondary">{statusLabel(t, invoice.status)}</Badge>
            )}

            <div className="flex-1" />

            {/* ⚠️ No *Szerkesztés* — ux-pass-2, 14 Oct: *"This should be inline like all other
                parts … no edit button needed. Same with Invoices."* */}
            {handlers.canManage ? (
              <>
                <MoreMenu
                  label={t('invoice.action.more')}
                  items={[
                    {
                      label: t('invoice.action.openTarget'),
                      icon: 'arrow-right',
                      onSelect: handlers.onOpenTarget,
                    },
                    { type: 'separator' },
                    {
                      label: t('invoice.action.delete'),
                      icon: 'trash',
                      destructive: true,
                      onSelect: handlers.onDelete,
                    },
                  ]}
                />
              </>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            {/* 17 Oct 2026 · a whole line for the number on a phone, so `LNEST/2026-000355` does not
                break at its slashes beside the target. */}
            <div className="min-w-0 flex-1 basis-full tabular-nums desktop:basis-auto">
              <InlineTitle
                value={invoice.externalId}
                canEdit={handlers.canManage}
                onSave={(externalId) => {
                  handlers.onPatch({ externalId });
                }}
              />
            </div>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground flex max-w-full min-w-0 items-center gap-1.5 text-[12px] transition-colors"
              onClick={handlers.onOpenTarget}
            >
              <Badge variant="secondary">
                {invoice.targetKind === 'line_item'
                  ? t('invoice.target.lineItem')
                  : t('invoice.target.occurrence')}
              </Badge>
              {/* ⚠️ The NAME on its own beside the badge, built here rather than sliced out of
                  `targetLabel` — a line item called *Riport · v2* would have made a split on the
                  separator produce half a name. */}
              <span className="truncate">
                {invoice.targetKind === 'line_item'
                  ? (invoice.lineItemName ?? t('invoice.target.gone'))
                  : `${invoice.contractIdentifier ?? ''} ${
                      invoice.occurrenceIndex === null
                        ? (invoice.occurrenceStartsOn ?? '')
                        : `#${String(invoice.occurrenceIndex)}`
                    }`.trim()}
              </span>
              <Icon name="arrow-right" size={12} />
            </button>
          </div>
        </>
      }
      rail={
        <>
          <RailCard title={t('invoice.amount')} padded>
            <div className="mt-1 text-[22px] leading-none tabular-nums">{amount}</div>
            <div className="text-muted-foreground mt-1.5 text-[11.5px] leading-relaxed">
              {t('invoice.amount.note')}
            </div>
          </RailCard>

          <RailCard title={t('invoice.details')}>
            {/**
             * ⚠️⚠️ **EVERY ROW IS ITS OWN CONTROL — ux-pass-2, 14 Oct.** The number is the title
             * above, so its row here reads it back rather than offering a second place to type it.
             * What the invoice bills is a kind and a source together, so that row opens a dialog
             * asking just that (`RailEditLink`); the arrow beside it still goes there.
             */}
            <RailRow label={t('invoice.field.externalId')}>
              <RailValue>{invoice.externalId}</RailValue>
            </RailRow>
            <RailRow label={t('invoice.field.amount')}>
              {handlers.canManage ? (
                <RailMoney
                  label={t('invoice.field.amount')}
                  value={invoice.amount}
                  suffix={symbol}
                  onCommit={(next) => {
                    /* ⚠️ An invoice has an amount, strictly positive — empty or zero reverts. The
                       cap is the server's, and its refusal names the figure left to bill. */
                    if (next !== null && next > 0) handlers.onPatch({ amount: next });
                  }}
                />
              ) : (
                <RailValue>{amount}</RailValue>
              )}
            </RailRow>
            <RailRow label={t('invoice.field.date')}>
              {handlers.canManage ? (
                <RailDate
                  label={t('invoice.field.date')}
                  value={invoice.issuedOn}
                  clearable={false}
                  onChange={(issuedOn) => {
                    if (issuedOn !== '' && issuedOn !== invoice.issuedOn) {
                      handlers.onPatch({ issuedOn });
                    }
                  }}
                />
              ) : (
                <RailValue>{invoice.issuedOn}</RailValue>
              )}
            </RailRow>
            <RailRow label={t('invoice.field.source')}>
              <RailEditLink
                value={targetLabel(t, invoice)}
                editLabel={t('invoice.field.target')}
                openLabel={t('invoice.action.openTarget')}
                {...(handlers.canManage ? { onEdit: handlers.onEditTarget } : {})}
                onOpen={handlers.onOpenTarget}
              />
            </RailRow>
            <RailRow label={t('invoice.field.owner')} divided>
              <RailPicker
                label={t('invoice.field.owner')}
                value={invoice.ownerName}
                before={
                  <PersonFace
                    name={invoice.ownerName}
                    seed={invoice.ownerAvatarSeed}
                    url={invoice.ownerAvatarUrl}
                    colorKey={invoice.ownerMemberId}
                  />
                }
                disabled={!handlers.canManage}
                searchPlaceholder={t('contract.owner.search')}
                items={data.members.map((one) => ({
                  label: one.name,
                  checked: one.id === invoice.ownerMemberId,
                  onSelect: () => {
                    if (one.id !== invoice.ownerMemberId) {
                      handlers.onPatch({ ownerMemberId: one.id });
                    }
                  },
                }))}
              />
            </RailRow>
            <RailRow label={t('invoice.field.created')}>
              <RailValue>{invoice.createdAt.slice(0, 10)}</RailValue>
            </RailRow>
          </RailCard>
        </>
      }
      main={
        <>
          <section>
            <SectionHeader title={t('invoice.note')} />
            <div className="mt-1.5">
              <InvoiceNote
                html={invoice.noteHtml}
                canEdit={handlers.canManage}
                onSave={(noteHtml) => {
                  handlers.onPatch({ noteHtml });
                }}
              />
            </div>
          </section>

          <section>
            <SectionHeader title={t('invoice.files')} />
            <div className="mt-1.5">
              <Attachments
                target="invoice"
                targetId={invoice.id}
                canEdit={handlers.canManage}
                emptyNote={t('invoice.files.empty')}
                onUpload={async (files) => {
                  for (const file of files) await handlers.onAttach(file);
                }}
              />
            </div>
          </section>

          <MetafieldsCard
            fields={data.metafields}
            values={invoice.metafields}
            members={data.members}
            canEdit={handlers.canManage}
            onSave={handlers.onMetafields}
          />

          {/* ux-pass-2 · the real audit trail, `/history/invoice/:id`, closed until opened. */}
          <CollapsibleHistory type="invoice" id={invoice.id} />
        </>
      }
    />
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The note — rich text, edited in place. ux-pass-2, 14 Oct.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Megjegyzés field should be a rich text editor like the comments are."* The task description's
 * shape exactly (`task-detail.tsx`'s `Description`): click the text to edit, `CommentComposer` with
 * `commitOnBlur` and a Mentés / Mégse pair, Escape reverts.
 *
 * ⚠️ It replaces a bare `Textarea` that wrote the WHOLE invoice on every keystroke — each with the
 * version it was rendered with, so the second letter of a word arrived stale.
 *
 * ⚠️⚠️ **THE HTML IS RENDERED WITH `dangerouslySetInnerHTML`, AND THAT IS SAFE FOR ONE REASON:
 * `invoice.service.ts` SANITISES ON WRITE** (`invoice-note.ts`, on the comments' allow-list), and
 * `0210` built the backfilled rows by escaping. If that ever weakens, this line is stored XSS.
 */
function InvoiceNote({
  html,
  canEdit,
  onSave,
}: {
  readonly html: string;
  readonly canEdit: boolean;
  readonly onSave: (html: string) => void;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);

  const body =
    html === '' ? (
      <span className="text-muted-foreground text-[12.5px]">{t('invoice.note.placeholder')}</span>
    ) : (
      <div
        className={`text-[12.5px] leading-relaxed ${COMMENT_PROSE}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );

  if (!canEdit) return body;

  if (editing) {
    return (
      <CommentComposer
        initialHtml={html}
        allowFiles={false}
        commitOnBlur
        footer
        placeholder={t('invoice.note.placeholder')}
        label={t('invoice.note')}
        submitLabel={t('common.save')}
        onCancel={() => {
          setEditing(false);
        }}
        onSubmit={(next) => {
          onSave(next);
          setEditing(false);
        }}
      />
    );
  }

  return (
    <Tooltip asChild content={t('invoice.note')}>
      <div
        role="button"
        tabIndex={0}
        className="hover:bg-accent/40 focus-visible:ring-ring/50 -mx-1.5 block w-full cursor-text rounded-md px-1.5 py-1 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
        onClick={(event) => {
          /* ⚠️ A link in the note opens, rather than the editor — the anchor is the target. */
          if ((event.target as HTMLElement).closest('a') !== null) return;
          setEditing(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          setEditing(true);
        }}
      >
        {body}
      </div>
    </Tooltip>
  );
}
